import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queryKeys";
import { useRealtimeCollection } from "@/lib/realtime";
import { configureWorker, fetchQueuedCards, fetchWorkers } from "./api";
import { summarize } from "./model";

/** Machines report while the page is open: realtime refreshes it, as on Ads Queue. */
export function useWorkers() {
  useRealtimeCollection("workers");
  return useQuery({
    queryKey: queryKeys.collection("workers"),
    queryFn: ({ signal }) => fetchWorkers(signal),
    refetchOnMount: "always",
  });
}

/** Keyed under flow_cards, so a card picked or queued anywhere refreshes the counts. */
export function useQueuedCards() {
  useRealtimeCollection("flow_cards");
  return useQuery({
    queryKey: queryKeys.collection("flow_cards", "workers-queued"),
    queryFn: ({ signal }) => fetchQueuedCards(signal),
    refetchOnMount: "always",
  });
}

export function useConfigureWorker() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: configureWorker,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collection("workers") });
    },
  });
}

/** "Offline" and elapsed times move on their own, so the page re-renders once a minute. */
export function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** How many machines are on a card right now, for the badge on the Workers link. Offline ones never count. */
export function useWorkingCount(): number {
  const workers = useWorkers();
  const now = useMinuteClock();
  return workers.data ? summarize(workers.data, now).working : 0;
}
